<template>
  <div id="reservation-form" class="reservation-form h-full" v-if="selectedReservation">
    <div class="mb-4 relative">
      <div id="reservation-name" class="value font-heading font-semibold text-4xl grow w-11/12">
        {{ displayName }}
      </div>

      <ReservationStatusBadge
        :status="selectedReservation.status"
        class="-translate-y-1/2 translate-x-3/4 absolute top-1/2 right-0"
      />
    </div>

    <div class="flex flex-wrap">
      <template v-if="showGuestCount">
        <div class="w-1/2 pr-2" id="reservation-guest-seating-time">
          <span class="font-heading font-semibold">
            {{ selectedReservation.number_of_guests }} guests
          </span>
          at
          <span class="font-semibold font-heading">
            {{ currentSeatingTime }}
          </span>
        </div>
      </template>
      <div id="selectedReservationTableNames" v-html="tableNames"></div>
    </div>

    <div
      v-if="tags.length > 0"
      id="reservation-tags"
      class="flex items-center justify-start flex-wrap gap-2 my-4"
    >
      <TagDefault
        v-for="tag in tags"
        :key="tag.id"
        :id="`reservation-tag-${tag.id}`"
        :tag="tag"
        type="long"
        selected
      />
    </div>

    <div
      v-if="selectedReservation.internal_notes"
      class="flex flex-col mt-2"
      id="reservation-notes"
    >
      <div class="font-label text-fg-secondary text-xs uppercase">Reservation Notes</div>
      <div class="font-sans text-base">{{ selectedReservation.internal_notes }}</div>
    </div>

    <div
      v-if="selectedReservation.contact && selectedReservation.contact.notes"
      class="flex flex-col mt-2"
      id="reservation-guest-notes"
    >
      <div class="font-label text-fg-secondary text-xs uppercase">Guest Notes</div>
      <div class="font-sans text-base">{{ selectedReservation.contact.notes }}</div>
    </div>

    <div class="flex flex-col mt-2" :class="{ hidden: hideFakeEmail && hideFakePhone }">
      <div class="font-sans text-base" :class="{ hidden: hideFakePhone }" id="reservation-phone">
        {{ selectedReservation.contact && selectedReservation.contact.phone }}
      </div>
      <div class="font-sans text-base" :class="{ hidden: hideFakeEmail }" id="reservation-email">
        {{ selectedReservation.contact && selectedReservation.contact.email }}
      </div>
      <div
        v-if="emailStatusLabel"
        id="email-status-label"
        class="text-alerts-warning text-sm flex justify-start items-center gap-1"
      >
        <ExclamationTriangleIcon class="w-4 h-4" />
        <span>{{ emailStatusLabel }}</span>
      </div>
    </div>

    <ReservationTransactions
      v-if="transactions.length > 0"
      id="reservation-transactions"
      class="section mt-8"
      :addOns="reservationAddOns"
      :addOnAmount="Number(selectedReservation.add_on_amount)"
      :promotion="selectedReservation.promotion_code"
      :transactions="transactions"
    />
  </div>
</template>

<script lang="ts">
import * as _ from 'lodash-es'
import { mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ExclamationTriangleIcon from '@/assets/exclamationTriangle.svg?component'
import TagDefault from '@/common/components/atoms/TagDefault.vue'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import ReservationStatusBadge from '@/modules/reservation/components/atoms/ReservationStatusBadge.vue'
import ReservationTransactions from '@/modules/reservation/components/molecules/ReservationTransactions.vue'
import { useSettingStore } from '@/stores/setting'
import { byId, sortBy } from '@/utils/utils'

import {
  EmailStatus,
  GuestTagRead,
  ReservationAddOnRead,
  ReservationTagRead,
  TransactionDetailedRead,
} from '@generated/types'

interface DecoratedTransaction extends TransactionDetailedRead {
  isManualEdit: boolean
}

interface ReservationAddOns extends ReservationAddOnRead {
  name: string
}

export default defineComponent({
  name: 'ReservationDetails',
  components: {
    TagDefault,
    ReservationStatusBadge,
    ReservationTransactions,
    ExclamationTriangleIcon,
  },
  async mounted() {
    await this.settingStore.loading
  },

  computed: {
    ...mapStores(useSettingStore),
    ...mapState(useSettingStore, ['currency']),
    ...mapState(usePerformanceReservationStore, [
      'selectedPerformance',
      'selectedTables',
      'selectedReservation',
    ]),

    reservationAddOns(): ReservationAddOns[] {
      return this.selectedReservation!.add_ons.map(selectedAddOn => {
        const performanceAddOn = this.selectedPerformance!.add_ons.find(
          byId(selectedAddOn.performance_add_on_id),
        )
        return {
          ...selectedAddOn,
          name: performanceAddOn ? performanceAddOn.name : '',
        }
      })
    },

    transactions(): DecoratedTransaction[] {
      return sortBy(this.selectedReservation!.transactions.slice(), 'created')
        .map(transaction => {
          const isManualEdit = !transaction.client_secret
          const tax = isManualEdit ? 0 : transaction.tax
          const service_fee = isManualEdit ? 0 : transaction.service_fee

          return { ...transaction, isManualEdit, tax, service_fee }
        })
        .filter(transaction => Number(transaction.amount) !== 0)
    },

    taxRate(): string {
      return this.settingStore.taxRateString(this.selectedReservation!)
    },

    currentSeatingTime(): string {
      const seatingTime = this.selectedPerformance!.seating_times.find(
        byId(this.selectedReservation!.seating_time_id),
      )
      if (!seatingTime) {
        return ''
      }
      return this.settingStore.timeStr(seatingTime.time)
    },

    displayName(): string {
      if (!this.selectedReservation!.contact) {
        return 'Walk In'
      }
      return this.selectedReservation!.contact.name
    },

    showGuestCount(): boolean {
      return this.selectedReservation!.number_of_guests > 0
    },

    hideFakePhone(): boolean {
      return (
        !this.selectedReservation!.contact ||
        this.selectedReservation!.contact.phone === '555-555-5555'
      )
    },

    hideFakeEmail(): boolean {
      return (
        !!this.selectedReservation!.contact?.email &&
        this.selectedReservation!.contact.email.includes('anonymous+')
      )
    },

    tableNames(): string {
      if (this.selectedTables.length === 1) {
        // This is `Table 1`
        return (
          'Table <span class="font-bold font-heading">' + this.selectedTables[0].name + '</span>'
        )
      } else if (this.selectedTables.length === 2) {
        // This is `Table 1 & Table 2`
        const firstTableName = this.selectedTables[0].name
        const secondTableName = this.selectedTables[1].name
        return (
          'Tables <span class="font-bold font-heading">' +
          firstTableName +
          '</span> & <span class="font-bold font-heading">' +
          secondTableName +
          '</span>'
        )
      } else {
        // This is `Table 1, Table 2, Table 3, Table N, & Table Final`
        const tableNames = this.selectedTables.map(table => table.name)
        const lastTableName = tableNames.pop()
        return (
          'Tables <span class="font-bold font-heading">' +
          tableNames.join('</span>, <span class="font-bold font-heading">') +
          '</span> & <span class="font-bold font-heading">' +
          lastTableName +
          '</span>'
        )
      }
    },

    emailStatusLabel(): string {
      if (!this.selectedReservation || !this.selectedReservation.contact.email_status) return ''
      else if (this.selectedReservation.contact.email_status === EmailStatus.BOUNCED)
        return 'This email has bounced.'
      else if (this.selectedReservation.contact.email_status === EmailStatus.SPAM)
        return 'Recipient has marked our email as spam.'
      else return ''
    },

    tags(): (GuestTagRead | ReservationTagRead)[] {
      const reservationTags =
        this.selectedReservation!.tags?.map(this.settingStore.getReservationTag) ?? []
      const guestTags =
        this.selectedReservation!.contact?.tags?.map(this.settingStore.getGuestTag) ?? []
      return _.compact(_.concat(reservationTags, guestTags))
    },
  },
})
</script>
